import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FilterTabs } from "@/components/NotificationBanner";
import Toast from "@/components/Toast";
import { ChevronLeft, Upload, CheckCircle, AlertCircle, Package, BoxIcon, Tag, Trash2, Printer, Loader2, Lock } from "lucide-react";
import { countLabelPages, printLabelFile } from "@/lib/labelPrint";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import RefreshButton from "@/components/RefreshButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = { withCredentials: true };
const STATE_STYLES = {
  NONE: "bg-slate-100 text-slate-500",
  READY: "bg-blue-100 text-blue-700",
  APPLIED: "bg-emerald-100 text-emerald-700",
};

export default function FBATeam() {
  const { t } = useLang();
  const [filterMode, setFilterMode] = useState("active");
  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [boxes, setBoxes] = useState([]);
  const [loadingBoxes, setLoadingBoxes] = useState(false);
  const [labelModal, setLabelModal] = useState(null); // { storeName, storeBoxes, file }
  const [labelSelected, setLabelSelected] = useState(new Set());
  const [uploading, setUploading] = useState(false);
  const [draggingStore, setDraggingStore] = useState(null);
  const [msg, setMsg] = useState(null);

  const clearMsg = useCallback(() => setMsg(null), []);
  const showMsg = (type, text) => setMsg({ type, text });
  const errorText = (e) => {
    const detail = e.response?.data?.detail;
    return typeof detail === "string" ? detail : t("recv.error");
  };

  const fetchWorks = useCallback(async (silent = false) => {
    try {
      const { data } = await axios.get(`${API}/pallet-work`, ax);
      setWorks(data.filter(w => (filterMode === "history"
        ? w.status === "COMPLETED"
        : ["BOXING", "BOXED"].includes(w.status) && w.ready_box_count > 0)));
    } catch {
      if (!silent) setMsg({ type: "error", text: t("recv.loading_error") });
    } finally {
      setLoading(false);
    }
  }, [filterMode, t]);

  useEffect(() => { setLoading(true); fetchWorks(); }, [fetchWorks]);

  // `reuseItems`: boxes already on screen keep their items (a ready box is locked), only new ones are fetched.
  const fetchBoxes = async (work, silent = false, reuseItems = false) => {
    if (!silent) setLoadingBoxes(true);
    try {
      const { data } = await axios.get(`${API}/pallets/${work.pallet_id}/boxes`, ax);
      const ready = data.filter(b => b.ready);
      const known = new Map(reuseItems ? boxes.map(b => [b.id, b.items]) : []);
      const withItems = await Promise.all(ready.map(async box => {
        if (known.has(box.id)) return { ...box, items: known.get(box.id) };
        try {
          return { ...box, items: (await axios.get(`${API}/boxes/${box.id}/items`, ax)).data };
        } catch { return { ...box, items: [] }; }
      }));
      setBoxes(withItems);
    } catch (e) {
      if (!silent) showMsg("error", errorText(e));
    } finally {
      if (!silent) setLoadingBoxes(false);
    }
  };

  const openWork = async (work) => { setSelected(work); setBoxes([]); await fetchBoxes(work); };
  const closeWork = () => { setSelected(null); setBoxes([]); fetchWorks(); };
  // Refresh: the list, or the open work and its boxes, without a loading flash. A manual run (button, or after an
  // action) re-reads every box's items; the background run only reads items of boxes it has not seen yet.
  const reloadScreen = async (manual = false) => {
    if (!selected) { await fetchWorks(true); return; }
    const { data } = await axios.get(`${API}/pallet-work`, ax);
    const fresh = data.find(w => w.id === selected.id);
    if (fresh) setSelected(fresh);
    await fetchBoxes(selected, true, !manual);
  };
  const reload = () => reloadScreen(true);
  const { refresh, refreshing } = useAutoRefresh(reloadScreen, { paused: !!labelModal || uploading || !!draggingStore });

  const readOnly = selected?.status === "COMPLETED";
  const labelledCount = boxes.filter(b => b.label_count > 0).length;
  const boxesByStore = boxes.reduce((acc, box) => {
    (acc[box.store_id] = acc[box.store_id] || { storeName: box.store_name, boxes: [] }).boxes.push(box);
    return acc;
  }, {});

  const openLabelModal = (storeName, storeBoxes, file) => {
    const open = storeBoxes.filter(b => b.label_state !== "APPLIED");
    if (open.length === 0) return;
    const unlabeled = open.filter(b => b.label_count === 0).map(b => b.id);
    setLabelSelected(new Set(unlabeled.length > 0 ? unlabeled : open.map(b => b.id)));
    setLabelModal({ storeName, storeBoxes: open, file, pages: null });
    countLabelPages(file).then(pages => setLabelModal(m => (m && m.file === file ? { ...m, pages } : m)));
  };
  // A file holds one label per box, or a label plus a shipping label per box.
  const pagesMismatch = labelModal?.pages != null && labelSelected.size > 0
    && labelModal.pages !== labelSelected.size && labelModal.pages !== labelSelected.size * 2;

  const toggleBox = (id) => setLabelSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const saveAssignment = async () => {
    if (!labelModal || labelSelected.size === 0) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", labelModal.file);
      form.append("box_ids", Array.from(labelSelected).join(","));
      await axios.post(`${API}/pallet-work/${selected.id}/labels`, form, ax);
      showMsg("success", t("mkt.assigned", { count: labelSelected.size }));
      setLabelModal(null);
      await reload();
    } catch (e) {
      showMsg("error", errorText(e));
    } finally {
      setUploading(false);
    }
  };

  const deleteLabel = async (labelId) => {
    try {
      await axios.delete(`${API}/labels/${labelId}`, ax);
      showMsg("success", t("mkt.label_removed"));
      await reload();
    } catch (e) {
      showMsg("error", errorText(e));
    }
  };

  // The other boxes covered by the same label file, so the tenant sees which boxes a file was assigned to.
  const sharedWith = (box, label) => {
    const others = boxes.filter(b => b.id !== box.id && b.labels.some(x => x.file_url === label.file_url));
    return others.length > 0 ? `+ ${others.map(b => b.box_name).join(", ")}` : "";
  };

  const printLabel = async (label) => {
    try {
      if (!(await printLabelFile(label.file_url))) showMsg("error", t("labels.popup_blocked"));
    } catch (e) {
      showMsg("error", errorText(e));
    }
  };

  if (selected) {
    return (
      <div className="space-y-4 pb-24">
        <Toast msg={msg} onClose={clearMsg} />
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="outline" size="sm" onClick={closeWork} className="gap-1.5 font-semibold border-slate-300 text-slate-700 hover:bg-slate-100">
            <ChevronLeft className="w-4 h-4" /> {t("common.back")}
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-slate-900 truncate">{selected.pallet_name}</h1>
            <p className="text-sm text-slate-500 truncate">{selected.destination_warehouse_name}</p>
          </div>
          <StatusBadge status={selected.status} />
          <RefreshButton onClick={refresh} refreshing={refreshing} />
          {boxes.length > 0 && (
            <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${labelledCount === boxes.length ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
              {t("mkt.card_progress", { labelled: labelledCount, count: boxes.length })}
            </span>
          )}
        </div>

        <div className={`flex items-start gap-2 p-3 rounded-xl border text-sm ${readOnly ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}>
          {readOnly ? <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />}
          <span>{t(readOnly ? "mkt.completed_banner" : "mkt.waiting_banner")}</span>
        </div>

        {loadingBoxes && (
          <div className="flex items-center justify-center py-12 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> {t("common.loading")}
          </div>
        )}

        {!loadingBoxes && boxes.length === 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-400">
            <BoxIcon className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="font-medium">{t("mkt.no_boxes")}</p>
          </div>
        )}

        {!loadingBoxes && Object.entries(boxesByStore).map(([storeId, { storeName, boxes: storeBoxes }]) => {
          const done = storeBoxes.filter(b => b.label_count > 0).length;
          const dragging = draggingStore === storeId;
          const uploadable = !readOnly && storeBoxes.some(b => b.label_state !== "APPLIED");
          return (
            <div key={storeId} className="space-y-2">
              <div className="flex items-center justify-between px-1">
                <h2 className="text-base font-bold text-slate-800 flex items-center gap-2">
                  <Tag className="w-4 h-4 text-purple-600" /> {storeName}
                </h2>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${done === storeBoxes.length ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                  {t("mkt.card_progress", { labelled: done, count: storeBoxes.length })}
                </span>
              </div>

              {uploadable && (
                <label
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-dashed cursor-pointer transition-all ${dragging ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50/40"}`}
                  data-testid={`store-upload-${storeId}`}
                  onDragEnter={e => { e.preventDefault(); setDraggingStore(storeId); }}
                  onDragOver={e => { e.preventDefault(); setDraggingStore(storeId); }}
                  onDragLeave={e => { e.preventDefault(); setDraggingStore(null); }}
                  onDrop={e => {
                    e.preventDefault(); setDraggingStore(null);
                    const file = e.dataTransfer.files?.[0];
                    if (file) openLabelModal(storeName, storeBoxes, file);
                  }}>
                  <input type="file" className="sr-only" accept=".pdf,.png,.jpg,.jpeg,.webp"
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (file) { openLabelModal(storeName, storeBoxes, file); e.target.value = ""; }
                    }} />
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${dragging ? "bg-blue-100" : "bg-slate-100"}`}>
                    <Upload className={`w-4 h-4 ${dragging ? "text-blue-600" : "text-slate-400"}`} />
                  </div>
                  <div>
                    <p className={`text-sm font-semibold ${dragging ? "text-blue-700" : "text-slate-600"}`}>
                      {t(dragging ? "mkt.drop_here" : "mkt.upload_for_store", { store: storeName })}
                    </p>
                    <p className="text-xs text-slate-400">{t("mkt.upload_hint")}</p>
                  </div>
                </label>
              )}

              {storeBoxes.map(box => (
                <div key={box.id} className={`bg-white rounded-2xl border shadow-sm overflow-hidden ${box.label_count > 0 ? "border-emerald-200" : "border-slate-200"}`}
                  data-testid={`marketplace-box-${box.id}`}>
                  <div className={`flex items-center justify-between gap-2 px-4 py-3 border-b ${box.label_count > 0 ? "bg-emerald-50 border-emerald-100" : "bg-slate-50 border-slate-100"}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <BoxIcon className={`w-4 h-4 shrink-0 ${box.label_count > 0 ? "text-emerald-600" : "text-slate-400"}`} />
                      <span className="font-bold text-slate-900 truncate">{box.box_name}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-500 shrink-0">
                      {box.weight && <span className="font-semibold text-slate-700">{box.weight} kg</span>}
                      {box.length && box.width && box.height && (
                        <span className="hidden sm:inline">{box.length}×{box.width}×{box.height} cm</span>
                      )}
                      <span className={`font-semibold px-2 py-0.5 rounded-full ${STATE_STYLES[box.label_state]}`}>
                        {t(`labels.state.${box.label_state}`)}
                      </span>
                    </div>
                  </div>

                  {box.items?.length > 0 && (
                    <div className="px-4 py-3 border-b border-slate-50 space-y-1.5">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{t("mkt.contents")}</p>
                      {box.items.map(i => (
                        <div key={i.id} className="flex items-center justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <span className="text-sm text-slate-800 truncate block">{i.product_name}</span>
                            <span className="text-xs font-mono text-slate-400">{i.asin}</span>
                          </div>
                          <span className="shrink-0 bg-slate-100 text-slate-800 font-bold text-sm px-2.5 py-1 rounded-lg">×{i.quantity}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {box.labels.length > 0 && (
                    <div className="px-4 py-3 space-y-1.5">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide flex items-center gap-1.5">
                        <Printer className="w-3.5 h-3.5" /> {t("mkt.labels")}
                      </p>
                      {box.labels.map((l, idx) => (
                        <div key={l.id} className="flex items-center gap-2 p-2.5 bg-emerald-50 border border-emerald-200 rounded-xl">
                          <button type="button" onClick={() => printLabel(l)} data-testid={`print-label-${l.id}`}
                            className="flex items-center gap-2 flex-1 min-w-0 hover:opacity-75 transition-opacity text-left">
                            <Printer className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
                            <span className="text-xs text-emerald-700 truncate">{l.original_filename || `${t("mkt.labels")} ${idx + 1}`}</span>
                            <span className="text-xs text-slate-400 truncate">{sharedWith(box, l)}</span>
                          </button>
                          {box.label_state === "APPLIED" || readOnly ? (
                            <span title={t("mkt.applied_lock")}><Lock className="w-3.5 h-3.5 text-slate-400" /></span>
                          ) : (
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-600 hover:bg-red-50 shrink-0"
                              onClick={() => deleteLabel(l.id)} data-testid={`delete-label-${l.id}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        })}

        <Dialog open={!!labelModal} onOpenChange={open => { if (!open) setLabelModal(null); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>{t("mkt.assign_title")}</DialogTitle>
              <p className="text-sm text-slate-500">{t("mkt.assign_body", { file: labelModal?.file?.name || "" })}</p>
            </DialogHeader>
            <div className="space-y-2">
              <button type="button" className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 text-sm font-semibold text-blue-600"
                onClick={() => {
                  const all = labelModal.storeBoxes.map(b => b.id);
                  setLabelSelected(prev => (prev.size === all.length ? new Set() : new Set(all)));
                }}>
                {t("mkt.select_all", { count: labelModal?.storeBoxes?.length || 0 })}
              </button>
              <div className="border-t border-slate-100 pt-2 space-y-1.5 max-h-60 overflow-y-auto">
                {labelModal?.storeBoxes?.map(box => {
                  const checked = labelSelected.has(box.id);
                  return (
                    <button key={box.id} type="button" onClick={() => toggleBox(box.id)} data-testid={`assign-box-${box.id}`}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left transition-all ${checked ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}>
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${checked ? "bg-blue-600 border-blue-600" : "border-slate-300"}`}>
                        {checked && <CheckCircle className="w-3 h-3 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-slate-900">{box.box_name}</p>
                        {box.label_count > 0 && <p className="text-xs text-slate-400">{t("mkt.already_labeled")}</p>}
                      </div>
                    </button>
                  );
                })}
              </div>
              {pagesMismatch && (
                <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-700" data-testid="pages-mismatch">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  {t("mkt.pages_mismatch", { pages: labelModal.pages, count: labelSelected.size, double: labelSelected.size * 2 })}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setLabelModal(null)}>{t("common.cancel")}</Button>
              <Button disabled={labelSelected.size === 0 || uploading} onClick={saveAssignment}
                className="bg-blue-600 hover:bg-blue-700" data-testid="assign-label-save">
                {uploading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {t("mkt.assign_save", { count: labelSelected.size })}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-4">
      <Toast msg={msg} onClose={clearMsg} />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>
          {t("nav.marketplace_amazon_fba")}
        </h1>
        <div className="flex items-center gap-2">
          <FilterTabs mode={filterMode} onChange={setFilterMode} labels={{ active: t("planning.active"), history: t("planning.history") }} />
          <RefreshButton onClick={refresh} refreshing={refreshing} />
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-slate-400">{t("common.loading")}</p>
      ) : works.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400">
          <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">{t("mkt.empty_title")}</p>
          <p className="text-sm mt-1">{t("mkt.empty_hint")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {works.map(w => (
            <div key={w.id} onClick={() => openWork(w)} data-testid={`marketplace-work-${w.id}`}
              className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-purple-300 transition-all cursor-pointer p-4 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-bold text-slate-900 truncate">{w.pallet_name}</h3>
                  <p className="text-xs text-slate-500 truncate">{w.destination_warehouse_name}</p>
                </div>
                <StatusBadge status={w.status} />
              </div>
              <p className="text-xs text-slate-500">{t("mkt.card_ready", { ready: w.ready_box_count, count: w.box_count })}</p>
              <p className={`text-xs font-semibold ${w.labelled_box_count === w.box_count ? "text-emerald-600" : "text-amber-600"}`}>
                {t("mkt.card_progress", { labelled: w.labelled_box_count, count: w.box_count })}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
